/* The persistent basket bar.

   Appears above the tab bar whenever there is anything in the basket, on every
   screen except the basket and checkout themselves — a bar that offers to take
   you where you already are is a dead control.

   It slides up from below rather than fading in, and dismisses back down the
   same way. Enter and exit share a path: something that arrives from the
   bottom and leaves through the top reads as two unrelated events.

   The total rolls rather than swapping, because the number changing *is* the
   feedback for the tap that changed it. */

import { Link, useLocation } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ShoppingBag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../state/cart'
import { usePrefs } from '../../state/prefs'
import { money } from '../../lib/format'
import { springSheet } from '../../design/motion'
import { RollingNumber } from '../../design/motion.tsx'

export function CartBar({ subtotal }: { subtotal: number }) {
  const { t } = useTranslation()
  const { locale } = usePrefs()
  const { count, isEmpty } = useCart()
  const { pathname } = useLocation()
  const reduce = useReducedMotion()

  const suppressed = /^\/(basket|checkout|auth|order\/)/.test(pathname)
  const show = !isEmpty && !suppressed

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="cartbar-wrap"
          initial={reduce ? { opacity: 0 } : { y: '130%' }}
          animate={reduce ? { opacity: 1 } : { y: 0 }}
          exit={reduce ? { opacity: 0 } : { y: '130%' }}
          transition={reduce ? { duration: 0.12 } : springSheet}
        >
          <Link to="/basket" className="cartbar">
            <span className="cartbar-badge" aria-hidden>
              <ShoppingBag size={17} />
              <span className="cartbar-count tnum">{count}</span>
            </span>

            <span className="cartbar-label">{t('basket.viewBasket')}</span>

            <span className="cartbar-total">
              <RollingNumber value={subtotal} format={(n) => money(n, locale)} />
            </span>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
